import { Shield, MessageSquare, Eye, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import transporterMohamed from "@/assets/transporter-mohamed.jpg";
import transporterYoussef from "@/assets/transporter-youssef.jpg";
import transporterKarim from "@/assets/transporter-karim.jpg";
import transporterHassan from "@/assets/transporter-hassan.jpg";

const solutions = [
  {
    icon: Shield,
    title: "Transporteurs vérifiés",
    description: "Processus de vérification structuré : identité, documents professionnels. Vous échangez avec des profils validés.",
  },
  {
    icon: MessageSquare,
    title: "Contact direct & simple",
    description: "Une fois mis en relation, échangez directement avec le transporteur. Pas d'intermédiaire, pas de complication.",
  },
  {
    icon: Eye,
    title: "Transparence totale",
    description: "Prix définis entre vous et le transporteur, conditions explicites, pas de mauvaise surprise.",
  },
  {
    icon: Star,
    title: "Avis clients",
    description: "Consultez les avis des autres clients. Des retours authentiques pour une décision éclairée.",
  },
];

const SolutionSection = () => {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="solution" className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div ref={leftRef} className={`transition-all duration-700 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              GoBledPro simplifie l'envoi de colis France ⇄ Maroc.
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-6">
              GoBledPro facilite la mise en relation entre clients et transporteurs indépendants, vérifiés et notés par d'autres clients.
            </p>

            {/* Solutions List */}
            <div className="space-y-4">
              {solutions.map((solution, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 group transition-all duration-500 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${200 + index * 100}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                    <solution.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-base font-heading font-semibold text-foreground mb-0.5">
                      {solution.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {solution.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className={`mt-6 transition-all duration-500 ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '600ms' }}>
              <Link to="/demande-transport">
                <Button size="lg" className="bg-secondary/90 hover:bg-secondary border border-secondary text-secondary-foreground font-semibold group shadow-md">
                  Essayer gratuitement
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Visual */}
          <div ref={rightRef} className={`relative transition-all duration-700 ${rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{ transitionDelay: '200ms' }}>
            <div className="bg-gradient-to-br from-secondary/10 to-primary/10 rounded-2xl p-6 md:p-8">
              {/* Fake app interface */}
              <div className="bg-card rounded-xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="bg-primary p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                      <span className="text-base">📦</span>
                    </div>
                    <div>
                      <p className="text-primary-foreground font-medium text-sm">Votre demande</p>
                      <p className="text-primary-foreground/70 text-xs">Paris → Casablanca</p>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-3 space-y-3">
                  {/* Transporter card */}
                  <div className="border border-border rounded-lg p-3 hover:border-secondary transition-colors">
                    <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full border-2 border-secondary/50 overflow-hidden">
                          <img src={transporterMohamed} alt="Mohamed K." className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="font-semibold text-card-foreground text-sm">Mohamed K.</p>
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                            <span className="text-xs text-muted-foreground">Transporteur vérifié</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 bg-secondary/10 px-2 py-0.5 rounded-full">
                        <Shield className="w-3 h-3 text-secondary" />
                        <span className="text-xs text-secondary font-medium">Vérifié</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Disponible cette semaine</p>
                      <Link to="/demande-transport">
                        <Button size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-7 font-medium shadow-sm">
                          Contacter
                        </Button>
                      </Link>
                    </div>
                  </div>
                  
                  {/* Other cards */}
                  <div className="border border-border rounded-lg p-3 opacity-60">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full border-2 border-primary/40 overflow-hidden">
                        <img src={transporterYoussef} alt="Youssef B." className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-card-foreground text-sm">Youssef B.</p>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs text-muted-foreground">Vérifié</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border border-border rounded-lg p-3 opacity-40">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full border-2 border-secondary/40 overflow-hidden">
                        <img src={transporterHassan} alt="Hassan M." className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-card-foreground text-sm">Hassan M.</p>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs text-muted-foreground">Vérifié</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border border-border rounded-lg p-3 opacity-25">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full border-2 border-primary/40 overflow-hidden">
                        <img src={transporterKarim} alt="Karim E." className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-card-foreground text-sm">Karim E.</p>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-xs text-muted-foreground">Vérifié</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
